import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api, { formatApiError, MASCOT } from "@/lib/api";
import { Lock, CreditCard, QrCode, ShieldCheck, RefreshCw } from "lucide-react";

export default function Checkout() {
  const [params] = useSearchParams();
  const planId = params.get("plan") || "pro";
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [method, setMethod] = useState("card");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/checkout/plans").then(({ data }) => {
      setPlan(data.plans.find((p) => p.id === planId) || data.plans[0]);
    });
  }, [planId]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan_id: plan.id, email: form.email, name: form.name, method,
        origin_url: window.location.origin,
      });
      if (method === "pix" && data.pix_available === false) {
        toast.info("Pix é ativado após reivindicar sua conta Stripe. Seguindo pelo checkout de teste.");
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao iniciar o pagamento");
      setLoading(false);
    }
  };

  if (!plan) return null;

  return (
    <div className="min-h-screen mesh-warm">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Resumo */}
          <div className="lg:col-span-2">
            <div className="rounded-3xl bg-white border border-[#e8d8c8] p-7 shadow-sm sticky top-24">
              <div className="flex items-center gap-3">
                <img src={MASCOT.tilt} alt="Solange" className="w-14 h-14 rounded-full object-cover border-2 border-[#c17f3e]" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#c17f3e] font-semibold">Plano escolhido</div>
                  <div className="font-display text-xl font-bold text-[#9c5f27]">{plan.name}</div>
                </div>
              </div>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="text-3xl font-bold font-display text-[#2e1e10]">R$ {plan.price.toFixed(2).replace(".", ",")}</span>
                <span className="text-[#8c735f]">/{plan.period}</span>
              </div>
              <ul className="mt-5 space-y-2 text-sm text-[#5c4634]">
                {plan.features.map((f) => <li key={f}>• {f}</li>)}
              </ul>
              <div className="mt-6 flex items-center gap-2 text-xs text-[#8c735f] bg-[#fdf8f3] rounded-xl p-3">
                <ShieldCheck size={16} className="text-[#f0791f]" />
                Pagamento seguro via Stripe. Ambiente de teste: cartão 4242 4242 4242 4242.
              </div>
            </div>
          </div>

          {/* Método */}
          <div className="lg:col-span-3">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#9c5f27]">Finalize sua assinatura</h1>
            <p className="mt-2 text-[#5c4634]">Após o pagamento você cria sua senha e já começa a usar.</p>

            <form onSubmit={submit} className="mt-6 space-y-5" data-testid="checkout-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Seu nome" testid="checkout-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Maria Vendedora" required />
                <Field label="E-mail" testid="checkout-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="voce@email.com" required />
              </div>

              <div>
                <span className="block text-sm font-medium text-[#5c4634] mb-2">Forma de pagamento</span>
                <div className="grid sm:grid-cols-2 gap-3">
                  <MethodCard
                    active={method === "card"} onClick={() => setMethod("card")} testid="method-card"
                    icon={CreditCard} title="Cartão recorrente"
                    desc="Cobra automaticamente todo mês. Cancele quando quiser."
                    badge={<span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#15803d]"><RefreshCw size={11} /> Recorrente</span>}
                  />
                  <MethodCard
                    active={method === "pix"} onClick={() => setMethod("pix")} testid="method-pix"
                    icon={QrCode} title="Pix (por ciclo)"
                    desc="Pagamento único de 30 dias. Renovação manual."
                  />
                </div>
              </div>

              <button type="submit" disabled={loading} className="btn-orange w-full py-4 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="checkout-submit">
                <Lock size={18} /> {loading ? "Redirecionando..." : `Pagar R$ ${plan.price.toFixed(2).replace(".", ",")}`}
              </button>
              <p className="text-center text-sm text-[#8c735f]">
                Já tem conta? <Link to="/login" className="text-[#f0791f] font-semibold">Entrar</Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function MethodCard({ active, onClick, icon: Icon, title, desc, badge, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`text-left rounded-2xl border-2 p-4 transition ${active ? "border-[#f0791f] bg-[#fff6ec]" : "border-[#e8d8c8] bg-white hover:border-[#c17f3e]"}`}>
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${active ? "bg-[#f0791f] text-white" : "bg-[#f5ebe1] text-[#9c5f27]"}`}><Icon size={20} /></div>
        {badge}
      </div>
      <div className="mt-3 font-semibold text-[#2e1e10]">{title}</div>
      <div className="text-xs text-[#8c735f] mt-0.5">{desc}</div>
    </button>
  );
}

function Field({ label, testid, type = "text", ...props }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#5c4634] mb-1.5">{label}</span>
      <input type={type} data-testid={testid}
        className="w-full rounded-xl border border-[#e8d8c8] bg-white px-4 py-3 text-[#2e1e10] outline-none focus:border-[#f0791f] focus:ring-2 focus:ring-[#f0791f]/20 transition"
        {...props} />
    </label>
  );
}
